import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ArrowRightIcon, CommandIcon, SettingsIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Box, Stack, Typography } from '@mui/material'

import { Button, Modal } from '@repo/ui'
import { KeyChip } from 'components/display'
import { useReferrerState, useShortcutBindings } from 'hooks'
import { SHORTCUT_CATALOG, formatBinding, isTypingTarget } from 'utils'

/**
 * The keyboard-shortcuts reference — a READ-ONLY listing of every binding.
 *
 * Ported from Vergence's `KeyboardShortcutsDialog`, including the split it exists
 * to enforce: this is reference, NOT an editor. Rebinding lives in Settings, and
 * the footer links there. Linear, GitHub, Notion and Slack all ship this shape,
 * and the reason is the same in each — the dialog is opened mid-task to answer
 * "what was that key again", so it must stay glanceable and dismissible. Recording
 * chrome (Change…, "press a key now", a conflict warning) turns a two-second
 * lookup into a modal with state.
 *
 * ## Three ways in, deliberately
 *
 *   • The sidebar Help popover's "Keyboard shortcuts" row — where a user who does
 *     not yet know the `?` binding will look.
 *   • The global `?` key, no modifier — the binding every one of the apps above
 *     uses, so it is the one an experienced user will try first. Suppressed while
 *     focus is in a field, or it would eat question marks the user is typing.
 *   • `openKeyboardShortcutsDialog()` — an imperative opener for any other surface.
 *
 * ## Why an event and not Context
 *
 * A provider + `useContext` would put a React dependency on every would-be caller
 * and re-render its whole subtree to carry a boolean that matters once a session.
 * The dialog is mounted once in `Layout`; callers fire a window event. Same choice
 * Vergence made, for the same reason.
 */

/** Exported so listeners and tests share one literal rather than re-typing it. */
export const KEYBOARD_SHORTCUTS_OPEN_EVENT = 'vera:open-keyboard-shortcuts'

/** Open the globally-mounted dialog from anywhere. Safe inside an event handler;
 *  a no-op if called before the window exists. */
export const openKeyboardShortcutsDialog = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(KEYBOARD_SHORTCUTS_OPEN_EVENT))
}

export const KeyboardShortcutsDialog = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // Live, so a binding changed in Settings is already correct the next time the
  // dialog is opened — no reload, and no stale copy captured at mount.
  const bindings = useShortcutBindings()
  // Where the user was when they opened the dialog, so Settings can offer a way back.
  // The dialog is opened MID-TASK ("what was that key again") from any page, which is
  // exactly the cross-surface reference the back link exists for — the same reason the
  // Help popover's "Documentation" row carries one.
  const referrer = useReferrerState()

  // `?` from anywhere. Toggles rather than only opening, so the same key that
  // summoned it dismisses it.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '?') return
      // Never steal a question mark someone is typing. `isTypingTarget` is the
      // same guard the sidebar's ⌘B uses — one definition of "the user is typing".
      if (isTypingTarget(event.target)) return

      event.preventDefault()
      setOpen(previous => !previous)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    const handleOpen = () => setOpen(true)

    window.addEventListener(KEYBOARD_SHORTCUTS_OPEN_EVENT, handleOpen)
    return () => window.removeEventListener(KEYBOARD_SHORTCUTS_OPEN_EVENT, handleOpen)
  }, [])

  // Straight to Settings -> Personal -> Controls, the page that owns rebinding —
  // not an anchor part-way down General, which would land the user somewhere they
  // still have to hunt for the thing they clicked through for.
  const goToSettings = () => {
    setOpen(false)
    navigate(ROUTES.SETTINGS_PERSONAL_CONTROLS, { state: referrer })
  }

  return (
    <Modal
      title="Keyboard shortcuts"
      // The same glyph the Help popover's row carries (`CommandIcon`), so the row and the
      // surface it opens are visibly one thing rather than two that happen to share a
      // name. This comment used to say that while the icon was a KeyboardIcon and the
      // popover's was a CommandIcon — one concept, two marks, in the two places a user
      // sees back to back.
      titleIcon={<CommandIcon size={20} strokeWidth={1} />}
      open={open}
      onClose={() => setOpen(false)}
      // The body is a list of short rows, not a form — the Modal's default 28px
      // bottom padding leaves a visible dead band above the footer here.
      bodySx={{ pt: 4, pb: 4 }}
      // MATCHES THE ⌘K PALETTE'S FOOTER — `px: 4, py: 2` there, so the two bottom
      // bars are the same height. They are the same kind of surface (a bordered bar
      // carrying a text hint and a tertiary action) and sat at visibly different
      // heights: this was `p: 5, pt: 4`, twenty and sixteen against the palette's
      // sixteen and eight.
      //
      // That earlier value was itself a fix — the Modal's default `p:4 / pt:3` sat
      // the row tight against the bottom edge once the link became a button with a
      // hit area. Both concerns are satisfied by copying the palette's numbers
      // rather than picking a third set: the button has room, and the two footers
      // agree.
      actionsSx={{ px: 4, py: 2 }}
      actions={
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
          sx={{ width: '100%' }}
        >
          <Typography variant="body3" color="text.secondary">
            Customize bindings or reset to defaults.
          </Typography>
          {/* The shared Button's `text` variant, NOT a hand-rolled coloured link.
              A bespoke link carries its own hover colour, font and padding, and
              drifts from every other tertiary action in the app — which is the
              drift §28 exists to stop. */}
          <Button
            variant="text"
            onClick={goToSettings}
            startIcon={<SettingsIcon size={16} strokeWidth={1} />}
            endIcon={<ArrowRightIcon size={14} strokeWidth={1} />}
          >
            Settings
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body3" color="text.secondary">
          Press <Box component="kbd" sx={{ fontFamily: 'Lato', fontWeight: 700 }}>?</Box> from anywhere
          to open this list.
        </Typography>

        {/* A flat list, no categories — every shortcut here is global, so any
            grouping would be invented rather than observed. Same call Vergence
            made when it deleted its two spurious buckets. */}
        <Stack component="ul" spacing={0} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {SHORTCUT_CATALOG.map(entry => (
            <Stack
              key={entry.key}
              component="li"
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={4}
              sx={{ py: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body3">{entry.label}</Typography>
                {/* Wraps rather than truncates: a description cut mid-word has
                    no reference value, and the dialog has the vertical room. */}
                <Typography variant="body4" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                  {entry.description}
                </Typography>
              </Box>
              <KeyChip>
                {entry.rebindable ? formatBinding(bindings[entry.key]) : entry.defaultBinding}
              </KeyChip>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Modal>
  )
}
